import {logExperiment} from '@homeaway/abacus-client-utils';

import {hasHPPageType, hasSLPPageType} from './pages';
import {hasEmptyDestinationValues, hasValidDestinationValues} from './destination';
import {hasEmptyDatesValues, hasValidDatesValues} from './dates';
import {hasEmptyGuestsValues} from './guests';

function getExperiment(experimentName, abacusStore) {
    return abacusStore.experimentData[experimentName];
}

/* istanbul ignore next */
function hasAllowableFullExposureExperiment() {
    return true;
}

function hasRunnableAnyVariantExperiment(experiment) {
    return experiment.value > 0;
}

function hasValidExperiment(experiment) {
    return !!experiment && !experiment.exception;
}

function hasAvailableExperiment(experimentName, abacusStore) {
    const experiment = getExperiment(experimentName, abacusStore);

    return hasValidExperiment(experiment);
}

function runExperiment(experimentName, experimentConfig, abacusStore) {
    /* istanbul ignore next */
    const {
        hasAllowableExperiment = hasAllowableFullExposureExperiment,
        hasRunnableExperiment = hasRunnableAnyVariantExperiment,
        shouldLogExperiment
    } = experimentConfig;

    const experiment = getExperiment(experimentName, abacusStore);

    if (hasValidExperiment(experiment) && hasAllowableExperiment(experiment)) {
        /* istanbul ignore else */
        if (shouldLogExperiment) {
            logExperiment(experiment);
        }

        return hasRunnableExperiment(experiment);
    }

    return false;
}

function runDatePickerSelectDateHintExperiment(experimentConfig, abacusStore) {
    return runExperiment('HA_PTA_SEARCHBOX_hintTextChoosingDates', experimentConfig, abacusStore);
}

function runGuestPickerAgeofChildrenExperiment(experimentConfig, abacusStore) {
    return runExperiment('HA_PTA_SEARCHBOX_addAgeOfChildren', experimentConfig, abacusStore);
}

function runGuestPickerAriaRoleExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_ARIArolesOnSearchForm', experimentConfig, abacusStore);
}

function runErrorStatesOnSearchFormExperiment(experimentConfig, abacusStore, systemOptions) {
    return hasHPPageType(systemOptions) && runExperiment('VRBO_LANDING_ErrorStatesOnSearchForm', experimentConfig, abacusStore);
}

function runDestinationlessErrorOnSearchFormExperiment(experimentConfig, abacusStore, systemOptions) {
    return !hasHPPageType(systemOptions) && runExperiment('VRBO_LANDING_DestinationlessErrorOnSearchForm', experimentConfig, abacusStore);
}

function showDestinationlessError(experimentConfig, abacusStore, formSettings, systemOptions, checkSubmitted = true) {
    const {
        hasSubmitted,
        wasLastSubmittedSuccessfully
    } = formSettings;

    const shouldRunErrorStatesOnSearchFormExperiment = runErrorStatesOnSearchFormExperiment(experimentConfig, abacusStore, systemOptions) ||
        runDestinationlessErrorOnSearchFormExperiment(experimentConfig, abacusStore, systemOptions);

    if (!checkSubmitted) {
        return shouldRunErrorStatesOnSearchFormExperiment;
    }

    return hasSubmitted && !wasLastSubmittedSuccessfully && shouldRunErrorStatesOnSearchFormExperiment;
}

function runCollapsedSearchformCTAExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_collpased_PDP_SF_CTA', experimentConfig, abacusStore);
}

function runDatesAndNumberOfNightsExperiment(experimentConfig, abacusStore) {
    return runExperiment('HA_PTA_SEARCHFORM_datesnumberOfNightsSummary', experimentConfig, abacusStore);
}

function runConsistentDayOfWeekExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_searchFormDatepickerDayOfWeek', experimentConfig, abacusStore);
}

function runSearchFormColourContrastAndLabelsExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_colourContrastOnSearchForm', experimentConfig, abacusStore);
}

function runSearchFormDateFormatExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_searchFormDateFormat', experimentConfig, abacusStore);
}

function runDatePickerSelectDateHintScaleExperiment(experimentConfig, abacusStore) {
    return runExperiment('VRBO_LANDING_scale_HintTextWhenChoosingDates', experimentConfig, abacusStore);
}

function runGuestPickerCTASearchExperiment(experimentConfig, abacusStore, latestDestinationValues, latestDatesValues) {
    return runExperiment('VRBO_LANDING_scale_launchSearchFromGuestPicker', {
        ...experimentConfig,
        hasAllowableExperiment() {
            return (
                hasValidDestinationValues(latestDestinationValues) &&
                    hasValidDatesValues(latestDatesValues)
            );
        }
    }, abacusStore);
}

function runHPPrePopulateCookieExperiment(experimentConfig, abacusStore, systemOptions, searchCookies) {
    return runExperiment('HA_HP_SEARCHFORM_PRE_POPULATE_COOKIE', {
        ...experimentConfig,
        hasAllowableExperiment() {
            const {
                destinationValues,
                datesValues,
                guestsValues
            } = searchCookies;

            return (
                hasHPPageType(systemOptions) && (
                    !hasEmptyDestinationValues(destinationValues) ||
                    !hasEmptyDatesValues(datesValues) ||
                    !hasEmptyGuestsValues(guestsValues)
                )
            );
        }
    }, abacusStore);
}

function isHPDestinationEmptySearchErrorExperimentAvailable(abacusStore, systemOptions, latestDestinationValues) {
    return (
        hasHPPageType(systemOptions) && (
            hasAvailableExperiment('HA_PTA_LANDING_DestinationlessError', abacusStore) &&
            !hasValidDestinationValues(latestDestinationValues)
        )
    );
}

function runHPDestinationEmptySearchErrorExperiment(experimentConfig, abacusStore, systemOptions, formSettings, latestDestinationValues) {
    return runExperiment('HA_PTA_LANDING_DestinationlessError', {
        ...experimentConfig,
        hasAllowableExperiment() {
            const {
                hasSubmitted,
                wasLastSubmittedSuccessfully
            } = formSettings;

            return (
                hasHPPageType(systemOptions) && (
                    !hasValidDestinationValues(latestDestinationValues) &&
                    hasSubmitted && !wasLastSubmittedSuccessfully
                )
            );
        }
    }, abacusStore);
}

function runHPGuestPickerCTASearchExperiment(experimentConfig, abacusStore, systemOptions, latestDestinationValues, latestDatesValues) {
    return runExperiment('HA_PTA_SEARCHBOX_launchSearchFromGuestPickerCTA', {
        ...experimentConfig,
        hasAllowableExperiment() {
            return (
                hasHPPageType(systemOptions) && (
                    hasValidDestinationValues(latestDestinationValues) &&
                    hasValidDatesValues(latestDatesValues)
                )
            );
        }
    }, abacusStore);
}

function runSLPDatePickerOpenedExperiment(experimentConfig, abacusStore, systemOptions) {
    return runExperiment('HA_PTA_SEARCHFORM_SLP_Datepickeropened', {
        ...experimentConfig,
        hasAllowableExperiment() {
            return hasSLPPageType(systemOptions);
        }
    }, abacusStore);
}

function checkDatePickerSelectDateHintExperiment(abacusStore, oldExperimentProps = {shouldLogExperiment: false}, scaledExperimentProps = {shouldLogExperiment: false}) {
    return (runDatePickerSelectDateHintExperiment(oldExperimentProps, abacusStore)
        || runDatePickerSelectDateHintScaleExperiment(scaledExperimentProps, abacusStore));
}

export {
    getExperiment,
    checkDatePickerSelectDateHintExperiment,
    hasAllowableFullExposureExperiment,
    hasRunnableAnyVariantExperiment,
    hasValidExperiment,
    hasAvailableExperiment,
    isHPDestinationEmptySearchErrorExperimentAvailable,
    runExperiment,
    runDatePickerSelectDateHintExperiment,
    runDatePickerSelectDateHintScaleExperiment,
    runGuestPickerAgeofChildrenExperiment,
    runGuestPickerAriaRoleExperiment,
    showDestinationlessError,
    runCollapsedSearchformCTAExperiment,
    runDatesAndNumberOfNightsExperiment,
    runConsistentDayOfWeekExperiment,
    runSearchFormColourContrastAndLabelsExperiment,
    runSearchFormDateFormatExperiment,
    runGuestPickerCTASearchExperiment,
    runHPPrePopulateCookieExperiment,
    runHPDestinationEmptySearchErrorExperiment,
    runHPGuestPickerCTASearchExperiment,
    runSLPDatePickerOpenedExperiment
};
